import AHDate from "_shared/dates/date";
import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";

type ProposalReviewerJSON = ProposalJSON["clientReviewers"][number];

const getProposalReviewerStateKey = (reviewer: ProposalReviewerJSON): string =>
  JSON.stringify([
    reviewer.userId,
    reviewer.legalEntityId,
    reviewer.isEditing ? "editing" : "comment-only",
    reviewer.dateApproved ?? "",
    reviewer.customMessage ?? "",
  ]);

export const proposalReviewersHaveSameState = (
  existingReviewers: readonly ProposalReviewerJSON[] | null | undefined,
  incomingReviewers: readonly ProposalReviewerJSON[] | null | undefined,
): boolean => {
  const existingState = (existingReviewers ?? [])
    .map(getProposalReviewerStateKey)
    .sort();
  const incomingState = (incomingReviewers ?? [])
    .map(getProposalReviewerStateKey)
    .sort();

  return (
    existingState.length === incomingState.length &&
    existingState.every(
      (reviewerState, index) => reviewerState === incomingState[index],
    )
  );
};

export const proposalReviewerStateHasChanged = (
  existingProposal: ProposalJSON | null | undefined,
  incomingProposal: ProposalJSON,
): boolean =>
  Boolean(
    existingProposal &&
    (!proposalReviewersHaveSameState(
      existingProposal.clientReviewers,
      incomingProposal.clientReviewers,
    ) ||
      !proposalReviewersHaveSameState(
        existingProposal.vendorReviewers,
        incomingProposal.vendorReviewers,
      )),
  );

/**
 * Draft-builder timestamps are generated by the browser whenever a builder is
 * cloned, so they cannot establish whether an incoming server snapshot is new.
 * Compare consecutive authoritative snapshots instead. Reviewer state is also
 * compared directly for legacy/equal-timestamp records where reviewer updates
 * did not advance lastUpdated.
 */
export const incomingProposalRequiresDraftBuilderRebase = (
  existingProposal: ProposalJSON | null | undefined,
  incomingProposal: ProposalJSON,
): boolean => {
  if (!existingProposal) return true;

  const existingLastUpdated = AHDate.fromJSON(existingProposal.lastUpdated);
  const incomingLastUpdated = AHDate.fromJSON(incomingProposal.lastUpdated);

  if (existingLastUpdated && incomingLastUpdated) {
    if (existingLastUpdated.isAfter(incomingLastUpdated)) return false;
    if (incomingLastUpdated.isAfter(existingLastUpdated)) return true;
  } else if (incomingLastUpdated && !existingLastUpdated) {
    return true;
  }

  return proposalReviewerStateHasChanged(existingProposal, incomingProposal);
};
